import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolStudio from '../../components/ToolStudio/ToolStudio';

const GALLERY = [
    { t: 'Pro Forma Pressure Test', d: 'Stress-tests a LIHTC deal to a clear go / no-go.', to: '/proforma-pressure-test' },
    { t: 'IT Cost Calculator', d: 'Right-sizes managed-IT spend in minutes.', to: '/it-cost-calculator' },
    { t: 'Cyber Security Scorecard', d: 'Grades your security posture instantly.', to: '/cyber-security-scorecard' },
    { t: 'Free Website Audit', d: 'Live performance + SEO audit of any site.', to: '/website-audit' },
    { t: 'TCAC 9% Scoring Estimator', d: 'Projects your tax-credit application score.', to: '/tcac-scoring-estimator' },
    { t: 'Cloud Migration Estimator', d: 'Scopes and prices a cloud move.', to: '/cloud-migration-estimator' },
    { t: 'AI Readiness Assessment', d: 'Scores how ready your ops are for AI.', to: '/ai-readiness-assessment' },
    { t: 'Area of Refuge Checker', d: 'Checks life-safety code compliance fast.', to: '/area-of-refuge-checker' },
    { t: 'Low-Voltage Budget', d: 'Builds a structured-cabling budget.', to: '/low-voltage-budget-estimator' },
];

const STEPS = [
    { n: '1', t: 'Describe the tool', d: 'Use the builder above — pick a type, tell us what it replaces, get an instant ballpark. Takes two minutes.' },
    { n: '2', t: 'We scope & quote', d: 'Within a business day you get a fixed scope, price and timeline — plus a couple of questions to sharpen it.' },
    { n: '3', t: 'We build & you launch', d: 'We design, build and test it, hand over a polished tool on your domain — branded to you, ready to use.' },
];

const TIERS = [
    { name: 'Spark', color: '#1F9D57', price: '$3k–$6k', wks: '2–3 weeks', for: 'A lead-magnet calculator or estimator', pts: ['White-labeled to your brand', 'Runs in any browser', 'Great for marketing & sales'] },
    { name: 'Engine', color: '#0044EB', price: '$7k–$15k', wks: '4–6 weeks', for: 'An internal tool that replaces a spreadsheet', pts: ['More inputs & logic', 'PDF export & saved results', 'Charts and reporting'], featured: true },
    { name: 'Platform', color: '#F44380', price: '$18k+', wks: '6–10 weeks', for: 'A multi-tool suite or client portal', pts: ['Logins & accounts', 'Integrations / API', 'Dashboards & data'] },
];

const FAQ = [
    { q: 'Who owns the tool?', a: 'You do — the design, the code and the domain. No lock-in, no per-seat fees.' },
    { q: 'Can it be white-labeled?', a: 'Yes. Every tool ships on your brand, your colors and your domain. Ours only appears if you want it to.' },
    { q: 'Do you host it?', a: 'Your call. Most tools are self-contained and drop onto your existing site; we can also host and maintain it for you.' },
    { q: "I don't know exactly what I need — is that ok?", a: "Completely. Pick “Not sure yet” in the builder, tell us the problem, and we’ll shape the right tool with you." },
    { q: 'How accurate is the instant estimate?', a: "It’s a genuine ballpark from the same ranges we quote against. We confirm the exact number on a short call before anything starts." },
];

const css = `
.tsp *{box-sizing:border-box}
.tsp__wrap{max-width:1180px;margin:0 auto;padding:0 clamp(16px,4vw,26px)}
.tsp__lead{text-align:center;max-width:760px;margin:0 auto;padding:clamp(30px,5vw,54px) 0 4px}
.tsp__k{font-family:'Axiforma Bold',sans-serif;color:#0044EB;font-size:13px;letter-spacing:2.4px;text-transform:uppercase}
.tsp__h2{font-family:'Axiforma Bold',sans-serif;color:#020842;font-size:clamp(27px,4.6vw,42px);line-height:1.08;margin:12px 0 0}
.tsp__sub{font-family:'Axiforma Regular',sans-serif;color:#54648A;font-size:clamp(15px,2.2vw,18px);line-height:1.55;margin:16px 0 0}
.tsp__sec{padding:clamp(34px,6vw,68px) 0}
.tsp__sech{text-align:center;max-width:680px;margin:0 auto clamp(26px,4vw,44px)}
.tsp__gal{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
.tsp__card{display:block;background:#fff;border:1px solid #E1E9F5;border-radius:14px;padding:20px 22px;text-decoration:none;transition:.15s}
.tsp__card:hover{border-color:#0044EB;box-shadow:0 16px 34px rgba(6,16,54,.10);transform:translateY(-2px)}
.tsp__cardt{font-family:'Axiforma Bold',sans-serif;color:#020842;font-size:18px;margin:0}
.tsp__cardd{font-family:'Axiforma Regular',sans-serif;color:#54648A;font-size:14.5px;line-height:1.45;margin:7px 0 12px}
.tsp__open{font-family:'Axiforma SemiBold',sans-serif;color:#0044EB;font-size:14px}
.tsp__steps{display:grid;grid-template-columns:repeat(3,1fr);gap:22px}
.tsp__step{background:#fff;border:1px solid #E1E9F5;border-radius:14px;padding:26px}
.tsp__sn{width:44px;height:44px;border-radius:12px;background:#020842;color:#fff;font-family:'Axiforma Bold',sans-serif;font-size:20px;display:flex;align-items:center;justify-content:center}
.tsp__st{font-family:'Axiforma Bold',sans-serif;color:#020842;font-size:20px;margin:16px 0 8px}
.tsp__sd{font-family:'Axiforma Regular',sans-serif;color:#54648A;font-size:15.5px;line-height:1.5;margin:0}
.tsp__tiers{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;align-items:stretch}
.tsp__tier{position:relative;background:#fff;border:1.5px solid #E1E9F5;border-radius:18px;padding:28px 26px;display:flex;flex-direction:column}
.tsp__tier--f{border-color:#0044EB;box-shadow:0 24px 54px rgba(0,68,235,.12)}
.tsp__flag{position:absolute;top:-12px;left:26px;background:#0044EB;color:#fff;font-family:'Axiforma Bold',sans-serif;font-size:11px;letter-spacing:1px;padding:6px 12px;border-radius:999px}
.tsp__tn{font-family:'Axiforma Bold',sans-serif;font-size:22px}
.tsp__tp{font-family:'Axiforma Bold',sans-serif;color:#020842;font-size:32px;margin:12px 0 2px}
.tsp__tw{font-family:'Axiforma SemiBold',sans-serif;color:#54648A;font-size:14px}
.tsp__tf{font-family:'Axiforma Regular',sans-serif;color:#16203C;font-size:15px;margin:14px 0 16px;padding:14px 0 0;border-top:1px solid #EEF2F8}
.tsp__tl{list-style:none;margin:0;padding:0}
.tsp__tl li{display:flex;gap:10px;font-family:'Axiforma Regular',sans-serif;color:#54648A;font-size:14.5px;padding:6px 0}
.tsp__tl svg{flex:none;margin-top:3px}
.tsp__faq{max-width:820px;margin:0 auto}
.tsp__qa{border-bottom:1px solid #E1E9F5;padding:20px 0}
.tsp__qq{font-family:'Axiforma Bold',sans-serif;color:#020842;font-size:18px;margin:0 0 8px}
.tsp__qaa{font-family:'Axiforma Regular',sans-serif;color:#54648A;font-size:15.5px;line-height:1.55;margin:0}
.tsp__note{text-align:center;font-family:'Axiforma Regular',sans-serif;color:#94A1BC;font-size:13px;margin-top:22px}
@media(max-width:860px){.tsp__steps,.tsp__tiers{grid-template-columns:1fr}}
`;

const Check = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4 4L19 7" stroke="#1F9D57" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// FAQPage structured data generated straight from the FAQ constant above, so
// the schema always matches what is visibly rendered on the page.
const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
};

const ToolStudioPage = () => {
    usePageSeo({
        title: 'Tool Studio — Custom Business Tool Builder',
        description:
            'BuildLab builds white-labeled business tools — calculators, estimators, configurators, and dashboards. Get an instant ballpark price and request a scoped quote.',
        keywords:
            'custom business tool, build a calculator, custom web tool, lead magnet calculator, spreadsheet to app, custom estimator, interactive tool development, white label calculator, BUILDLAB',
    });

    return (
        <Fragment>
            <Header />
            <main className="page_content tsp">
                <style dangerouslySetInnerHTML={{ __html: css }} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }} />
                <PageTitle pageTitle={'Tool Studio'} pagesub={'Custom Business Tools'} pageTop={'BUILDLAB'} />

                <section style={{ background: '#fff' }}>
                    <div className="tsp__wrap">
                        <div className="tsp__lead">
                            <div className="tsp__k">Innovative tools, built to order</div>
                            <h2 className="tsp__h2">You've used our free tools. Now get one built for your business.</h2>
                            <p className="tsp__sub">
                                Turn a clunky spreadsheet or a manual, repetitive decision into a polished interactive tool —
                                a calculator, estimator, checker or dashboard, white-labeled to your brand. Describe it below and
                                get an instant ballpark.
                            </p>
                        </div>
                    </div>
                </section>

                {/* the builder */}
                <section style={{ background: '#fff' }}>
                    <div className="tsp__wrap" style={{ paddingBottom: 'clamp(30px,5vw,56px)' }}>
                        <ToolStudio />
                    </div>
                </section>

                {/* proof gallery */}
                <section className="tsp__sec" style={{ background: '#F1F6FC' }}>
                    <div className="tsp__wrap">
                        <div className="tsp__sech">
                            <div className="tsp__k">Proof, not promises</div>
                            <h2 className="tsp__h2">Tools we've already built</h2>
                            <p className="tsp__sub">A few of the 20+ interactive tools live on this very site — across housing finance, IT, security and operations.</p>
                        </div>
                        <div className="tsp__gal">
                            {GALLERY.map((g) => (
                                <Link key={g.to} to={g.to} className="tsp__card">
                                    <p className="tsp__cardt">{g.t}</p>
                                    <p className="tsp__cardd">{g.d}</p>
                                    <span className="tsp__open">Open the tool →</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                {/* how it works */}
                <section className="tsp__sec" style={{ background: '#fff' }}>
                    <div className="tsp__wrap">
                        <div className="tsp__sech">
                            <div className="tsp__k">How it works</div>
                            <h2 className="tsp__h2">From idea to launched tool in weeks</h2>
                        </div>
                        <div className="tsp__steps">
                            {STEPS.map((s) => (
                                <div key={s.n} className="tsp__step">
                                    <div className="tsp__sn">{s.n}</div>
                                    <h3 className="tsp__st">{s.t}</h3>
                                    <p className="tsp__sd">{s.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* pricing tiers */}
                <section className="tsp__sec" style={{ background: '#F1F6FC' }}>
                    <div className="tsp__wrap">
                        <div className="tsp__sech">
                            <div className="tsp__k">Ballpark pricing</div>
                            <h2 className="tsp__h2">Fixed-scope packages</h2>
                            <p className="tsp__sub">Most tools land in one of three tiers. The builder above matches yours automatically.</p>
                        </div>
                        <div className="tsp__tiers">
                            {TIERS.map((t) => (
                                <div key={t.name} className={'tsp__tier' + (t.featured ? ' tsp__tier--f' : '')}>
                                    {t.featured && <span className="tsp__flag">MOST REQUESTED</span>}
                                    <div className="tsp__tn" style={{ color: t.color }}>{t.name}</div>
                                    <div className="tsp__tp">{t.price}</div>
                                    <div className="tsp__tw">{t.wks}</div>
                                    <div className="tsp__tf">{t.for}</div>
                                    <ul className="tsp__tl">
                                        {t.pts.map((p) => (<li key={p}><Check />{p}</li>))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        <p className="tsp__note">Ballpark ranges — your exact price is fixed in writing after a short scoping call.</p>
                    </div>
                </section>

                {/* faq */}
                <section className="tsp__sec" style={{ background: '#fff' }}>
                    <div className="tsp__wrap">
                        <div className="tsp__sech">
                            <div className="tsp__k">Good to know</div>
                            <h2 className="tsp__h2">Questions, answered</h2>
                        </div>
                        <div className="tsp__faq">
                            {FAQ.map((f) => (
                                <div key={f.q} className="tsp__qa">
                                    <h3 className="tsp__qq">{f.q}</h3>
                                    <p className="tsp__qaa">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ToolStudioPage;
